import React, { useState, useEffect, useContext, useCallback, useRef } from 'react';
import api from '../services/api';
import { motion, AnimatePresence } from 'framer-motion';
import { AuthContext } from '../contexts/AuthContext';
import Softphone from '../components/Softphone';
import NumbersList from '../components/NumbersList';
import Voicemail from '../components/Voicemail';
import VideoCall from '../components/VideoCall';
import Settings from '../components/Settings';
import Help from '../components/Help';
import OnboardingWizard from '../components/OnboardingWizard';
import UpgradeModal from '../components/UpgradeModal';
import { useSubscription, PLANS } from '../contexts/SubscriptionContext';
import { useNavigate } from 'react-router-dom';

const EASE = [0.22, 1, 0.36, 1];

// ─────────────────────────────────────────────────────────────────────────────
// ICONS
// ─────────────────────────────────────────────────────────────────────────────

function Ico({ children, className = 'h-5 w-5' }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"
      className={className} aria-hidden="true">{children}</svg>
  );
}
function DialerIcon({ className }) {
  return <Ico className={className}><rect x="4" y="4" width="4" height="4" rx="1"/><rect x="10" y="4" width="4" height="4" rx="1"/><rect x="16" y="4" width="4" height="4" rx="1"/><rect x="4" y="10" width="4" height="4" rx="1"/><rect x="10" y="10" width="4" height="4" rx="1"/><rect x="16" y="10" width="4" height="4" rx="1"/><rect x="4" y="16" width="4" height="4" rx="1"/><rect x="10" y="16" width="4" height="4" rx="1"/><rect x="16" y="16" width="4" height="4" rx="1"/></Ico>;
}
function HashIcon({ className }) {
  return <Ico className={className}><line x1="4" y1="9" x2="20" y2="9"/><line x1="4" y1="15" x2="20" y2="15"/><line x1="10" y1="3" x2="8" y2="21"/><line x1="16" y1="3" x2="14" y2="21"/></Ico>;
}
function VoicemailIcon({ className }) {
  return <Ico className={className}><circle cx="6" cy="12" r="3"/><circle cx="18" cy="12" r="3"/><path d="M9 12h6"/></Ico>;
}
function VideoIcon({ className }) {
  return <Ico className={className}><polygon points="23 7 16 12 23 17 23 7"/><rect x="1" y="5" width="15" height="14" rx="2" ry="2"/></Ico>;
}
function HelpIcon({ className }) {
  return <Ico className={className}><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><path d="M12 17h.01"/></Ico>;
}
function GearIcon({ className }) {
  return <Ico className={className}><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1Z"/></Ico>;
}
function SunIcon({ className }) {
  return <Ico className={className}><circle cx="12" cy="12" r="4"/><path d="M12 2v2M12 20v2m-7.07-14.07 1.41 1.41M17.66 17.66l1.41 1.41M2 12h2m16 0h2m-4.93 7.07-1.41-1.41M6.34 6.34 4.93 4.93"/></Ico>;
}
function MoonIcon({ className }) {
  return <Ico className={className}><path d="M21 12.8A9 9 0 1 1 11.2 3 7 7 0 0 0 21 12.8Z"/></Ico>;
}
function LogoutIcon({ className }) {
  return <Ico className={className}><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></Ico>;
}

// ─────────────────────────────────────────────────────────────────────────────
// DUAL CLOCK
// ─────────────────────────────────────────────────────────────────────────────

const TIMEZONES = [
  // ── Local ──────────────────────────────────────────────
  { label: 'Local (browser)',       value: 'local' },
  // ── UTC ────────────────────────────────────────────────
  { label: 'UTC / GMT',             value: 'UTC' },
  // ── United States ──────────────────────────────────────
  { label: 'EST — New York',        value: 'America/New_York' },
  { label: 'CST — Chicago',         value: 'America/Chicago' },
  { label: 'MST — Denver',          value: 'America/Denver' },
  { label: 'MST — Phoenix (no DST)',value: 'America/Phoenix' },
  { label: 'PST — Los Angeles',     value: 'America/Los_Angeles' },
  { label: 'AKST — Anchorage',      value: 'America/Anchorage' },
  { label: 'HST — Honolulu',        value: 'Pacific/Honolulu' },
  // ── Canada ─────────────────────────────────────────────
  { label: 'AST — Halifax',         value: 'America/Halifax' },
  { label: 'EST — Toronto',         value: 'America/Toronto' },
  { label: 'CST — Winnipeg',        value: 'America/Winnipeg' },
  { label: 'MST — Edmonton',        value: 'America/Edmonton' },
  { label: 'PST — Vancouver',       value: 'America/Vancouver' },
  // ── Latin America ───────────────────────────────────────
  { label: 'Mexico City',           value: 'America/Mexico_City' },
  { label: 'Bogotá / Lima',         value: 'America/Bogota' },
  { label: 'São Paulo / BRT',       value: 'America/Sao_Paulo' },
  { label: 'Buenos Aires',          value: 'America/Argentina/Buenos_Aires' },
  { label: 'Santiago',              value: 'America/Santiago' },
  // ── Europe ─────────────────────────────────────────────
  { label: 'London / GMT',          value: 'Europe/London' },
  { label: 'Lisbon',                value: 'Europe/Lisbon' },
  { label: 'Paris / CET',           value: 'Europe/Paris' },
  { label: 'Berlin / CET',          value: 'Europe/Berlin' },
  { label: 'Amsterdam',             value: 'Europe/Amsterdam' },
  { label: 'Madrid',                value: 'Europe/Madrid' },
  { label: 'Rome',                  value: 'Europe/Rome' },
  { label: 'Stockholm',             value: 'Europe/Stockholm' },
  { label: 'Warsaw',                value: 'Europe/Warsaw' },
  { label: 'Kyiv / EET',            value: 'Europe/Kyiv' },
  { label: 'Helsinki / EET',        value: 'Europe/Helsinki' },
  { label: 'Athens / EET',          value: 'Europe/Athens' },
  { label: 'Istanbul',              value: 'Europe/Istanbul' },
  { label: 'Moscow / MSK',          value: 'Europe/Moscow' },
  // ── Africa ─────────────────────────────────────────────
  { label: 'Cairo / EET',           value: 'Africa/Cairo' },
  { label: 'Lagos / WAT',           value: 'Africa/Lagos' },
  { label: 'Nairobi / EAT',         value: 'Africa/Nairobi' },
  { label: 'Johannesburg / SAST',   value: 'Africa/Johannesburg' },
  // ── Middle East ────────────────────────────────────────
  { label: 'Riyadh / AST',          value: 'Asia/Riyadh' },
  { label: 'Dubai / GST',           value: 'Asia/Dubai' },
  { label: 'Doha / AST',            value: 'Asia/Qatar' },
  { label: 'Tel Aviv / IST',        value: 'Asia/Jerusalem' },
  { label: 'Tehran / IRST',         value: 'Asia/Tehran' },
  // ── Asia ───────────────────────────────────────────────
  { label: 'Karachi / PKT',         value: 'Asia/Karachi' },
  { label: 'Mumbai / IST',          value: 'Asia/Kolkata' },
  { label: 'Colombo / SLST',        value: 'Asia/Colombo' },
  { label: 'Dhaka / BST',           value: 'Asia/Dhaka' },
  { label: 'Bangkok / ICT',         value: 'Asia/Bangkok' },
  { label: 'Singapore / SGT',       value: 'Asia/Singapore' },
  { label: 'Kuala Lumpur / MYT',    value: 'Asia/Kuala_Lumpur' },
  { label: 'Hong Kong / HKT',       value: 'Asia/Hong_Kong' },
  { label: 'Shanghai / CST',        value: 'Asia/Shanghai' },
  { label: 'Taipei / CST',          value: 'Asia/Taipei' },
  { label: 'Seoul / KST',           value: 'Asia/Seoul' },
  { label: 'Tokyo / JST',           value: 'Asia/Tokyo' },
  { label: 'Jakarta / WIB',         value: 'Asia/Jakarta' },
  { label: 'Yangon / MMT',          value: 'Asia/Rangoon' },
  // ── Australia / Pacific ────────────────────────────────
  { label: 'Perth / AWST',          value: 'Australia/Perth' },
  { label: 'Darwin / ACST',         value: 'Australia/Darwin' },
  { label: 'Adelaide / ACST',       value: 'Australia/Adelaide' },
  { label: 'Brisbane / AEST',       value: 'Australia/Brisbane' },
  { label: 'Sydney / AEST',         value: 'Australia/Sydney' },
  { label: 'Melbourne / AEST',      value: 'Australia/Melbourne' },
  { label: 'Auckland / NZST',       value: 'Pacific/Auckland' },
  { label: 'Fiji / FJT',            value: 'Pacific/Fiji' },
];

const DEFAULT_CLOCKS = [
  { id: 'clk1', label: 'Local', tz: 'local' },
  { id: 'clk2', label: 'PST',   tz: 'America/Los_Angeles' },
];

// ─────────────────────────────────────────────────────────────────────────────
// FUEL GAUGE  — car dashboard style (semicircle + needle)
// ─────────────────────────────────────────────────────────────────────────────

function FuelGauge({ theme }) {
  const isDark = theme === 'dark';
  const [balance, setBalance]     = useState(null);
  const [connected, setConnected] = useState(false);
  const [error, setError]         = useState(false);

  const fetchBalance = useCallback(async () => {
    try {
      const res = await api.get('/carrier/balance');
      setConnected(res.data.connected);
      setBalance(res.data.balance);
      setError(false);
    } catch {
      setError(true);
    }
  }, []);

  useEffect(() => {
    fetchBalance();
    const t = setInterval(fetchBalance, 5 * 60 * 1000);
    return () => clearInterval(t);
  }, [fetchBalance]);

  if (!connected || error || balance === null) return null;

  const MAX      = 100;
  const pct      = Math.min(Math.max(balance / MAX, 0), 1);
  const low      = balance < 20;
  // Estimate minutes at ~$0.005/min (Telnyx US inbound rate)
  const estMins  = Math.round(balance / 0.005);
  const minsStr  = estMins >= 1000
    ? `~${(estMins / 1000).toFixed(1)}k min`
    : `~${estMins} min`;

  // ── SVG geometry ──────────────────────────────────────────────────────────
  const W = 100, H = 62;
  const cx = 50, cy = 56;
  const rMid   = 38;
  const trkW   = 9;
  const rTick  = { inner: rMid - trkW / 2 - 3, outer: rMid + trkW / 2 + 4 };
  const needleLen = 30;

  const pt = (θ) => {
    const r = θ * Math.PI / 180;
    return [cx + rMid * Math.cos(r), cy - rMid * Math.sin(r)];
  };

  const [e120x, e120y] = pt(120);
  const [e60x,  e60y ] = pt(60);
  const needleRotDeg   = -(1 - pct) * 180;

  // ── Theme tokens ──────────────────────────────────────────────────────────
  const trackBg    = isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.10)';
  const tickStroke = isDark ? 'rgba(255,255,255,0.20)' : 'rgba(0,0,0,0.15)';
  const efColor    = isDark ? 'rgba(255,255,255,0.35)' : 'rgba(0,0,0,0.35)';
  const hubFill    = isDark ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.15)';
  const needleCol  = isDark ? '#ffffff'  : '#1e293b';
  const balCol     = low ? '#ef4444' : isDark ? '#ffffff' : '#111827';
  const subCol     = isDark ? 'rgba(255,255,255,0.40)' : 'rgba(0,0,0,0.40)';
  const pumpCol    = low ? '#ef4444' : isDark ? 'rgba(255,255,255,0.55)' : 'rgba(0,0,0,0.40)';

  const tickLine = (θ) => {
    const r = θ * Math.PI / 180;
    return {
      x1: cx + rTick.inner * Math.cos(r), y1: cy - rTick.inner * Math.sin(r),
      x2: cx + rTick.outer * Math.cos(r), y2: cy - rTick.outer * Math.sin(r),
    };
  };

  const SCALE = 0.72;

  return (
    <a
      href="https://portal.telnyx.com/#/billing/payment"
      target="_blank"
      rel="noopener noreferrer"
      title={`Telnyx balance: $${balance.toFixed(2)} — ${Math.round(pct * 100)}% of $${MAX}. Click to top up.`}
      style={{
        display: 'flex', alignItems: 'center', gap: 6,
        cursor: 'pointer', userSelect: 'none', textDecoration: 'none',
      }}
    >
      {/* ── Gauge SVG ── */}
      <svg
        width={Math.round(W * SCALE)}
        height={Math.round(H * SCALE)}
        viewBox={`0 0 ${W} ${H}`}
        overflow="visible"
        style={{ flexShrink: 0 }}
      >
        {/* Arc track background */}
        <path
          d={`M ${cx - rMid},${cy} A ${rMid},${rMid} 0 0,1 ${cx + rMid},${cy}`}
          fill="none" stroke={trackBg} strokeWidth={trkW} strokeLinecap="round"
        />

        {/* Red zone: E → 120° */}
        <path
          d={`M ${cx - rMid},${cy} A ${rMid},${rMid} 0 0,1 ${e120x},${e120y}`}
          fill="none" stroke="#ef4444" strokeWidth={trkW} strokeLinecap="butt" opacity="0.85"
        />
        {/* Amber zone: 120° → 60° */}
        <path
          d={`M ${e120x},${e120y} A ${rMid},${rMid} 0 0,1 ${e60x},${e60y}`}
          fill="none" stroke="#f59e0b" strokeWidth={trkW} strokeLinecap="butt" opacity="0.85"
        />
        {/* Green zone: 60° → F */}
        <path
          d={`M ${e60x},${e60y} A ${rMid},${rMid} 0 0,1 ${cx + rMid},${cy}`}
          fill="none" stroke="#22c55e" strokeWidth={trkW} strokeLinecap="round" opacity="0.85"
        />

        {/* Tick marks */}
        {[0, 45, 90, 135, 180].map((θ) => {
          const { x1, y1, x2, y2 } = tickLine(θ);
          const major = θ === 0 || θ === 90 || θ === 180;
          return (
            <line key={θ} x1={x1} y1={y1} x2={x2} y2={y2}
              stroke={tickStroke} strokeWidth={major ? 1.8 : 1.2} strokeLinecap="round"
            />
          );
        })}

        {/* E / F labels */}
        <text x={cx - rMid - 8} y={cy + 2} textAnchor="middle"
          fill={efColor} fontSize="8" fontWeight="800" fontFamily="system-ui,sans-serif">E</text>
        <text x={cx + rMid + 8} y={cy + 2} textAnchor="middle"
          fill={efColor} fontSize="8" fontWeight="800" fontFamily="system-ui,sans-serif">F</text>

        {/* Fuel pump icon at top-center of arc */}
        {/* Simple pump silhouette centered at (cx, cy - rMid - 2) */}
        <g transform={`translate(${cx - 5}, ${cy - rMid - 12})`} fill={pumpCol}>
          {/* pump body */}
          <rect x="1" y="3" width="6" height="8" rx="1" />
          {/* nozzle arm */}
          <rect x="7" y="4" width="3" height="1.5" rx="0.5" />
          {/* nozzle tip */}
          <rect x="9.5" y="3" width="1.5" height="4" rx="0.5" />
          {/* pump top */}
          <rect x="1" y="1" width="6" height="2" rx="0.5" />
        </g>

        {/* Needle */}
        <g
          transform={`translate(${cx},${cy}) rotate(${needleRotDeg})`}
          style={{ transition: 'transform 1s cubic-bezier(0.34,1.56,0.64,1)' }}
        >
          <line x1={-5} y1={1} x2={needleLen - 2} y2={1}
            stroke="rgba(0,0,0,0.20)" strokeWidth="2.5" strokeLinecap="round" />
          <line x1={-5} y1={0} x2={needleLen} y2={0}
            stroke={needleCol} strokeWidth="2" strokeLinecap="round" />
        </g>

        {/* Center hub */}
        <circle cx={cx} cy={cy} r="5.5" fill={hubFill} />
        <circle cx={cx} cy={cy} r="2.8" fill={needleCol} />
      </svg>

      {/* ── Readout ── */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        <span style={{
          fontSize: 13, fontWeight: 700, lineHeight: 1,
          color: balCol, fontVariantNumeric: 'tabular-nums',
          display: 'flex', alignItems: 'center', gap: 4,
        }}>
          ${balance.toFixed(2)}
          {low && (
            <span style={{
              fontSize: 8, fontWeight: 800, color: '#ef4444',
              background: 'rgba(239,68,68,0.15)', borderRadius: 3,
              padding: '1px 3px', letterSpacing: '0.05em',
            }}>LOW</span>
          )}
        </span>
        <span style={{
          fontSize: 10, fontWeight: 500, lineHeight: 1,
          color: subCol, fontVariantNumeric: 'tabular-nums',
        }}>
          {minsStr}
        </span>
      </div>
    </a>
  );
}

function DualClock({ theme }) {
  const isDark = theme === 'dark';

  const [clocks, setClocks] = useState(() => {
    try {
      const saved = localStorage.getItem('pbx_dual_clocks');
      return saved ? JSON.parse(saved) : DEFAULT_CLOCKS;
    } catch { return DEFAULT_CLOCKS; }
  });

  const [now, setNow]         = useState(new Date());
  const [editing, setEditing] = useState(null); // clock id or null
  const popoverRef            = useRef(null);

  // Tick every second
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  // Click-outside closes popover
  useEffect(() => {
    if (!editing) return;
    const handler = (e) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target)) {
        setEditing(null);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [editing]);

  const updateClock = (id, patch) => {
    const updated = clocks.map(c => c.id === id ? { ...c, ...patch } : c);
    setClocks(updated);
    try { localStorage.setItem('pbx_dual_clocks', JSON.stringify(updated)); } catch {}
  };

  const formatTime = (tz) => {
    const opts = { hour: '2-digit', minute: '2-digit', hour12: true };
    try {
      if (tz === 'local') return now.toLocaleTimeString(undefined, opts);
      return now.toLocaleTimeString(undefined, { ...opts, timeZone: tz });
    } catch { return '--:--'; }
  };

  // Theme tokens
  const labelColor  = isDark ? 'text-white/35' : 'text-slate-400';
  const timeColor   = isDark ? 'text-white/85'  : 'text-slate-700';
  const hoverBg     = isDark ? 'hover:bg-white/[0.06]' : 'hover:bg-slate-100';
  const activeBg    = isDark ? 'bg-white/[0.08]' : 'bg-slate-100';
  const popBg       = isDark ? 'bg-[#0d1117]/95 border-white/15' : 'bg-white border-slate-200';
  const inputCls    = isDark
    ? 'bg-white/10 text-white placeholder-white/30 border-white/15 focus:border-white/30'
    : 'bg-slate-50 text-slate-900 placeholder-slate-400 border-slate-200 focus:border-slate-400';
  const selectCls   = isDark
    ? 'bg-[#1a1f2e] text-white border-white/15'
    : 'bg-white text-slate-900 border-slate-200';
  const subLabel    = isDark ? 'text-white/35' : 'text-slate-400';

  return (
    <div className="hidden sm:flex items-center gap-1">
      {clocks.map((clock) => {
        const isOpen = editing === clock.id;
        return (
          <div key={clock.id} className="relative">
            {/* Clock button */}
            <motion.button
              whileTap={{ scale: 0.97 }}
              onClick={() => setEditing(isOpen ? null : clock.id)}
              title={`Click to customize — ${TIMEZONES.find(t => t.value === clock.tz)?.label || clock.tz}`}
              className={`flex flex-col items-start px-2.5 py-1.5 rounded-xl transition cursor-pointer select-none ${
                isOpen ? activeBg : hoverBg
              }`}
            >
              <span className={`text-[9px] font-bold uppercase tracking-[0.12em] leading-none ${labelColor}`}>
                {clock.label || '—'}
              </span>
              <span className={`text-[13px] font-mono font-semibold leading-tight mt-[3px] ${timeColor}`}>
                {formatTime(clock.tz)}
              </span>
            </motion.button>

            {/* Settings popover */}
            <AnimatePresence>
              {isOpen && (
                <motion.div
                  ref={popoverRef}
                  initial={{ opacity: 0, y: -8, scale: 0.96 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: -8, scale: 0.96 }}
                  transition={{ ease: EASE, duration: 0.18 }}
                  className={`absolute top-full left-0 mt-2 z-50 w-52 rounded-2xl border p-4 shadow-2xl backdrop-blur-xl ${popBg}`}
                >
                  <div className="space-y-3">
                    {/* Label */}
                    <div>
                      <p className={`text-[10px] font-bold uppercase tracking-widest mb-1.5 ${subLabel}`}>Label</p>
                      <input
                        autoFocus
                        value={clock.label}
                        onChange={e => updateClock(clock.id, { label: e.target.value })}
                        onKeyDown={e => (e.key === 'Enter' || e.key === 'Escape') && setEditing(null)}
                        placeholder="e.g. PST, Home, NYC…"
                        maxLength={12}
                        className={`w-full rounded-xl px-3 py-2 text-sm outline-none border transition ${inputCls}`}
                      />
                    </div>

                    {/* Timezone */}
                    <div>
                      <p className={`text-[10px] font-bold uppercase tracking-widest mb-1.5 ${subLabel}`}>Timezone</p>
                      <select
                        value={clock.tz}
                        onChange={e => updateClock(clock.id, { tz: e.target.value })}
                        className={`w-full rounded-xl px-3 py-2 text-sm outline-none border ${selectCls}`}
                      >
                        {TIMEZONES.map(tz => (
                          <option key={tz.value} value={tz.value}>{tz.label}</option>
                        ))}
                      </select>
                    </div>

                    {/* Done button */}
                    <motion.button
                      whileTap={{ scale: 0.97 }}
                      onClick={() => setEditing(null)}
                      className={`w-full py-2 rounded-xl text-sm font-semibold transition ${
                        isDark
                          ? 'bg-white/10 hover:bg-white/15 text-white'
                          : 'bg-slate-900 hover:bg-slate-700 text-white'
                      }`}
                    >
                      Done
                    </motion.button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        );
      })}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// DASHBOARD
// ─────────────────────────────────────────────────────────────────────────────

export default function Dashboard() {
  const [activeTab, setActiveTab]           = useState('softphone');
  const [incomingCall, setIncomingCall]     = useState(false);
  const [theme, setTheme]                   = useState('dark');
  const [commandOpen, setCommandOpen]       = useState(false);
  const [pstnBanner, setPstnBanner]         = useState(null);
  const [unreadVoicemails, setUnreadVoicemails]   = useState(0);
  const [showWizard, setShowWizard]               = useState(false);
  const { logout }                          = useContext(AuthContext);
  const { canAccess, tier, planInfo }        = useSubscription();
  const navigate                            = useNavigate();
  const [upgradeFeature, setUpgradeFeature] = useState(null);

  // Check carrier connection on mount — show wizard if not connected
  useEffect(() => {
    api.get('/carrier/status')
      .then(res => {
        if (!res.data.connected) setShowWizard(true);
      })
      .catch(() => {});
  }, []);

  // Fetch unread voicemail count on mount
  useEffect(() => {
    api.get('/voicemails', { params: { limit: 50 } })
      .then(res => {
        const count = (res.data.data || []).filter(v => !v.listened).length;
        setUnreadVoicemails(count);
      })
      .catch(() => {});
  }, []);

  // Clear badges when tabs are opened
  useEffect(() => {
    if (activeTab === 'voicemail') setUnreadVoicemails(0);
  }, [activeTab]);

  const handleIncomingCallChange = useCallback((isIncoming) => {
    setIncomingCall(isIncoming);
    if (isIncoming) setActiveTab('softphone');
  }, []);

  const pstnRingtoneRef = useRef(null);
  const pstnRingIntervalRef = useRef(null);
  const pstnNotificationRef = useRef(null);

  const stopPstnRingtone = useCallback(() => {
    if (pstnRingIntervalRef.current) {
      clearInterval(pstnRingIntervalRef.current);
      pstnRingIntervalRef.current = null;
    }
    if (pstnNotificationRef.current) {
      try { pstnNotificationRef.current.close(); } catch (_) {}
      pstnNotificationRef.current = null;
    }
    if (pstnRingtoneRef.current) {
      try { pstnRingtoneRef.current.close(); } catch (_) {}
      pstnRingtoneRef.current = null;
    }
  }, []);

  const startPstnRingtone = useCallback(() => {
    stopPstnRingtone();
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      pstnRingtoneRef.current = ctx;
      const playRing = () => {
        const vol = Math.max(0, Math.min(1, parseFloat(localStorage.getItem('pbx_ring_volume') ?? '0.5')));
        if (vol === 0) return;
        const now = ctx.currentTime;
        [0, 0.18, 0.36].forEach(offset => {
          const osc = ctx.createOscillator();
          const gain = ctx.createGain();
          osc.type = 'sine';
          osc.frequency.value = 440;
          gain.gain.setValueAtTime(vol, now + offset);
          gain.gain.exponentialRampToValueAtTime(0.001, now + offset + 0.12);
          osc.connect(gain).connect(ctx.destination);
          osc.start(now + offset);
          osc.stop(now + offset + 0.12);
        });
      };
      playRing();
      pstnRingIntervalRef.current = setInterval(playRing, 2500);
    } catch (e) { /* audio not available */ }
  }, [stopPstnRingtone]);

  const handlePstnIncoming = useCallback((banner) => {
    if (banner) {
      setPstnBanner(banner);
      startPstnRingtone();

      // Fire a browser notification so the call is visible even when the user
      // is on a different tab. requireInteraction keeps it until they act on it.
      try {
        const fireNotification = () => {
          if (typeof Notification === 'undefined' || Notification.permission !== 'granted') return;
          const n = new Notification('📞 Incoming Call', {
            body: `From: ${banner.from}`,
            icon: '/LOGO.png',
            requireInteraction: true,
            tag: 'pstn-incoming',
          });
          n.onclick = () => { window.focus(); n.close(); };
          pstnNotificationRef.current = n;
        };
        if (typeof Notification !== 'undefined') {
          if (Notification.permission === 'granted') {
            fireNotification();
          } else if (Notification.permission !== 'denied') {
            Notification.requestPermission().then((perm) => {
              if (perm === 'granted') fireNotification();
            });
          }
        }
      } catch { /* notifications blocked or unavailable */ }
    } else {
      setPstnBanner(null);
      stopPstnRingtone();
    }
  }, [startPstnRingtone, stopPstnRingtone]);

  const [pstnAnswerPending, setPstnAnswerPending] = useState(false);

  const handlePstnAnswer = useCallback(async () => {
    if (!pstnBanner?.callControlId) return;
    stopPstnRingtone();
    // Set flag BEFORE the API call so the ref is synced in Softphone before the
    // Verto notification arrives (backend sleeps 400ms then transfers; Verto lands
    // before the HTTP response returns — the old placement was a timing race).
    setPstnAnswerPending(true);
    setTimeout(() => setPstnAnswerPending(false), 15_000);
    try {
      const token = sessionStorage.getItem('token') || localStorage.getItem('pbx_token');
      await fetch('/api/calls/answer-pstn', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ callControlId: pstnBanner.callControlId }),
      });
    } catch (e) { console.error('PSTN answer failed', e); }
    setPstnBanner(null);
  }, [pstnBanner, stopPstnRingtone]);

  const handlePstnDecline = useCallback(async () => {
    if (!pstnBanner?.callControlId) return;
    stopPstnRingtone();
    try {
      const token = sessionStorage.getItem('token') || localStorage.getItem('pbx_token');
      await fetch('/api/calls/decline-pstn', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ callControlId: pstnBanner.callControlId }),
      });
    } catch (e) { console.error('PSTN decline failed', e); }
    setPstnBanner(null);
  }, [pstnBanner, stopPstnRingtone]);

  React.useEffect(() => {
    function onKey(e) {
      const mod = e.metaKey || e.ctrlKey;
      if (mod && e.key.toLowerCase() === 'k') { e.preventDefault(); setCommandOpen((v) => !v); }
      if (mod && e.key.toLowerCase() === 'l') { e.preventDefault(); setTheme((t) => t === 'dark' ? 'light' : 'dark'); }
      if (e.key === 'Escape') setCommandOpen(false);
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const TAB_GATE = { videocall: 'video', voicemail: 'voicemail' };

  const handleTabClick = (id) => {
    const feature = TAB_GATE[id];
    if (feature && !canAccess(feature)) {
      setUpgradeFeature(feature);
      return;
    }
    setActiveTab(id);
  };

  const tabs = [
    { id: 'softphone', icon: DialerIcon,    label: 'Softphone' },
    { id: 'help',      icon: HelpIcon,      label: 'Help' },
    { id: 'settings',  icon: GearIcon,      label: 'Settings' },
  ];

  const COMMANDS = [
    { label: 'Go to Softphone',  action: () => setActiveTab('softphone') },
    { label: 'Go to Numbers',    action: () => setActiveTab('numbers') },
    { label: 'Go to Video Call',  action: () => setActiveTab('videocall') },
    { label: 'Go to Voicemail',  action: () => setActiveTab('voicemail') },
    { label: 'Go to Settings',   action: () => setActiveTab('settings') },
    { label: 'Toggle theme ⌘L',  action: () => setTheme((t) => t === 'dark' ? 'light' : 'dark') },
    { label: 'Logout',           action: logout },
  ];

  // Theme tokens
  const bg          = theme === 'dark' ? 'bg-[#020305] text-white'     : 'bg-[#F5F7FA] text-slate-900';
  const nav         = theme === 'dark' ? 'bg-black/60 border-white/10' : 'bg-white border-slate-200';
  const panel       = theme === 'dark' ? 'bg-black/40 border-white/10' : 'bg-white border-slate-200';
  const soft        = theme === 'dark' ? 'bg-white/5'                  : 'bg-slate-100';
  const muted       = theme === 'dark' ? 'opacity-50'                  : 'text-slate-500';
  const iconActive  = theme === 'dark' ? 'bg-white/10 text-white'      : 'bg-slate-900 text-white';
  const iconIdle    = theme === 'dark' ? 'text-white/50 hover:bg-white/8 hover:text-white' : 'text-slate-400 hover:bg-slate-100 hover:text-slate-700';
  const contentCard = theme === 'dark' ? 'bg-black/40 border-white/10' : 'bg-white border-slate-200';

  return (
    <div className={`h-screen flex flex-col overflow-hidden ${bg}`}>

      {/* ── PSTN incoming call — fixed center overlay, above everything ──────── */}
      <AnimatePresence>
        {pstnBanner && (
          <motion.div
            key="pstn-incoming-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            style={{ position: 'fixed', inset: 0, zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            className="bg-black/75 backdrop-blur-xl"
          >
            <motion.div
              initial={{ scale: 0.85, y: 28 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.85, y: 28 }}
              transition={{ type: 'spring', stiffness: 300, damping: 26 }}
              className={`rounded-3xl border p-8 w-80 text-center shadow-2xl ${panel}`}
            >
              {/* Pulsing rings */}
              <div className="flex justify-center mb-6">
                <div className="relative flex items-center justify-center w-20 h-20">
                  <span className="absolute inset-0 rounded-full bg-green-500/20 animate-ping" />
                  <span className="absolute inset-2 rounded-full bg-green-500/15 animate-ping" style={{ animationDelay: '0.25s' }} />
                  <div className="relative w-20 h-20 rounded-full bg-green-500/20 border-2 border-green-400/50 flex items-center justify-center text-3xl select-none">
                    📞
                  </div>
                </div>
              </div>

              <p className={`text-xs font-semibold uppercase tracking-widest mb-2 ${muted}`}>
                Incoming Call
              </p>
              <p className={`text-2xl font-bold mb-1 ${theme === 'dark' ? 'text-white' : 'text-slate-900'}`}>
                {pstnBanner.from}
              </p>
              <p className={`text-xs mb-7 ${muted}`}>PSTN · Answer on your softphone</p>

              <div className="flex gap-3">
                <button
                  onClick={handlePstnDecline}
                  className="flex-1 py-3.5 rounded-2xl bg-red-500/20 hover:bg-red-500/30 text-red-400 border border-red-500/25 font-semibold text-sm transition"
                >
                  Decline
                </button>
                <button
                  onClick={handlePstnAnswer}
                  className="flex-1 py-3.5 rounded-2xl bg-green-500/20 hover:bg-green-500/30 text-green-400 border border-green-500/25 font-semibold text-sm transition"
                >
                  Answer
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Command Palette ─────────────────────────────────────────────────── */}
      <AnimatePresence>
        {commandOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-xl"
            onClick={() => setCommandOpen(false)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.96, y: 10 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.96, y: 10 }}
              transition={{ ease: EASE }}
              className={`w-[520px] rounded-2xl border p-6 shadow-2xl ${panel}`}
              onClick={(e) => e.stopPropagation()}
            >
              <input
                autoFocus
                placeholder="Type a command…"
                className={`w-full rounded-xl px-4 py-3 outline-none text-lg ${soft}`}
                onKeyDown={(e) => e.key === 'Escape' && setCommandOpen(false)}
              />
              <div className="mt-4 space-y-1">
                {COMMANDS.map((cmd, i) => (
                  <motion.button
                    key={i}
                    whileHover={{ x: 4 }}
                    onClick={() => { cmd.action(); setCommandOpen(false); }}
                    className={`w-full text-left px-4 py-2.5 rounded-xl text-sm transition ${soft} ${muted}`}
                  >
                    {cmd.label}
                  </motion.button>
                ))}
              </div>
              <div className={`mt-4 pt-4 border-t ${theme === 'dark' ? 'border-white/10' : 'border-slate-100'} text-xs ${muted} flex gap-4`}>
                <span>⌘K toggle</span><span>⌘L theme</span><span>⌘↩ call/answer</span><span>⌘D dial</span><span>Esc close</span>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Top Nav Bar ──────────────────────────────────────────────────────── */}
      <header className={`relative z-40 flex-shrink-0 flex items-center justify-between px-6 h-16 border-b backdrop-blur-xl ${nav}`}>

        {/* Left: logo + dual clocks */}
        <div className="flex items-center gap-4">
          <img
            src="/LOGO.png"
            alt="Only Number"
            className="h-10 object-contain flex-shrink-0"
            onError={e => { e.currentTarget.style.display = 'none'; }}
          />
          {/* Vertical divider */}
          <div className={`w-px h-6 hidden sm:block ${theme === 'dark' ? 'bg-white/10' : 'bg-slate-200'}`} />
          <DualClock theme={theme} />
          {/* Vertical divider */}
          <div className={`w-px h-6 hidden sm:block ${theme === 'dark' ? 'bg-white/10' : 'bg-slate-200'}`} />
          <FuelGauge theme={theme} />
        </div>

        {/* Right: nav tabs + theme toggle + logout */}
        <div className="flex items-center gap-2">
          <nav className="flex items-center gap-1">
            {tabs.map(({ id, icon: TabIcon, label }) => (
              <motion.button
                key={id}
                whileTap={{ scale: 0.95 }}
                onClick={() => handleTabClick(id)}
                title={label}
                className={
                  id === 'softphone'
                    ? `relative flex items-center justify-center w-11 h-11 rounded-2xl transition ${activeTab === id ? iconActive : iconIdle}`
                    : `relative flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-medium transition ${activeTab === id ? iconActive : iconIdle}`
                }
              >
                <TabIcon className={id === 'softphone' ? 'h-6 w-6' : 'h-4 w-4'} />
                {id !== 'softphone' && <span className="hidden md:inline">{label}</span>}

                {/* Incoming call badge */}
                {id === 'softphone' && incomingCall && activeTab !== 'softphone' && (
                  <span className="absolute -right-1 -top-1 flex h-3 w-3">
                    <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-green-400 opacity-75" />
                    <span className="relative inline-flex h-3 w-3 rounded-full bg-green-500" />
                  </span>
                )}

                {/* Unread voicemail badge */}
                {id === 'voicemail' && unreadVoicemails > 0 && activeTab !== 'voicemail' && (
                  <span className="absolute -right-1 -top-1 flex h-3 w-3">
                    <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-red-400 opacity-75" />
                    <span className="relative inline-flex h-3 w-3 rounded-full bg-red-500" />
                  </span>
                )}

                {/* Lock badge for gated tabs */}
                {TAB_GATE[id] && !canAccess(TAB_GATE[id]) && (
                  <span className="absolute -right-1 -top-1 flex h-3.5 w-3.5 items-center justify-center rounded-full bg-gray-700 border border-gray-600">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"
                      strokeLinecap="round" strokeLinejoin="round" className="h-2 w-2 text-gray-400">
                      <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                      <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                    </svg>
                  </span>
                )}

              </motion.button>
            ))}
          </nav>

          {/* Vertical divider */}
          <div className={`w-px h-6 ${theme === 'dark' ? 'bg-white/10' : 'bg-slate-200'}`} />

          {/* Theme toggle — single icon */}
          <motion.button
            whileTap={{ scale: 0.9 }}
            onClick={() => setTheme((t) => t === 'dark' ? 'light' : 'dark')}
            title={theme === 'dark' ? 'Light mode ⌘L' : 'Dark mode ⌘L'}
            className={`w-8 h-8 flex items-center justify-center rounded-xl transition ${iconIdle}`}
          >
            {theme === 'dark'
              ? <SunIcon className="h-4 w-4" />
              : <MoonIcon className="h-4 w-4" />}
          </motion.button>

          <motion.button
            whileTap={{ scale: 0.95 }}
            onClick={logout}
            title="Logout"
            className={`w-9 h-9 flex items-center justify-center rounded-xl transition hover:bg-red-500/15 hover:!opacity-100 ${iconIdle}`}
          >
            <LogoutIcon className="h-4 w-4" />
          </motion.button>
        </div>
      </header>

      {/* ── Main content ─────────────────────────────────────────────────────── */}
      <main className="flex-1 overflow-auto p-6">

        {/* Softphone always mounted — CSS hidden when on another tab */}
        <div
          className="flex flex-col"
          style={{
            display: activeTab === 'softphone' ? 'flex' : 'none',
            height: 'calc(100vh - 112px)',
          }}
        >
          <div className="flex-1 min-h-0">
            <Softphone onIncomingCallChange={handleIncomingCallChange} onPstnIncoming={handlePstnIncoming} theme={theme} pstnAnswerPending={pstnAnswerPending} onPstnAnswerHandled={() => setPstnAnswerPending(false)} onVoicemail={() => setActiveTab('voicemail')} unreadVoicemails={unreadVoicemails} onVideoCall={() => setActiveTab('videocall')} />
          </div>
        </div>

        {activeTab === 'videocall' && (
          <div className={`rounded-[32px] border p-6 backdrop-blur-xl relative overflow-hidden ${contentCard}`}
            style={{ height: 'calc(100vh - 112px)' }}>
            <VideoCall theme={theme} />
          </div>
        )}
        {activeTab === 'voicemail' && (
          <div className={`rounded-[32px] border p-6 backdrop-blur-xl ${contentCard}`}>
            <Voicemail theme={theme} onUnreadCount={setUnreadVoicemails} />
          </div>
        )}
        {activeTab === 'help' && (
          <div className={`rounded-[32px] border p-6 backdrop-blur-xl overflow-hidden ${contentCard}`}
            style={{ height: 'calc(100vh - 112px)' }}>
            <Help theme={theme} />
          </div>
        )}
        {activeTab === 'settings' && (
          <div className={`rounded-[32px] border p-6 backdrop-blur-xl ${contentCard}`}>
            <Settings theme={theme} />
            {/* Billing section */}
            <div className={`mt-6 pt-6 border-t ${theme === 'dark' ? 'border-gray-700' : 'border-gray-200'}`}>
              <div className="flex items-center justify-between mb-1">
                <div>
                  <p className={`text-sm font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>Billing & Plan</p>
                  <p className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                    Current plan: <span className="font-medium" style={{ color: planInfo?.color }}>{planInfo?.name}</span>
                    {planInfo?.price > 0 ? ` · $${planInfo.price}/mo` : ' · Free'}
                  </p>
                </div>
                <button
                  onClick={() => navigate('/pricing')}
                  className="px-4 py-2 rounded-xl bg-purple-600 hover:bg-purple-500 text-white text-sm font-medium transition-all">
                  {tier === 'free' ? 'Upgrade' : 'Manage plan'}
                </button>
              </div>
            </div>

            {/* Phone service reconfigure button */}
            <div className={`mt-6 pt-6 border-t ${theme === 'dark' ? 'border-gray-700' : 'border-gray-200'}`}>
              <div className="flex items-center justify-between">
                <div>
                  <p className={`text-sm font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>Phone Service</p>
                  <p className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>Reconfigure your Telnyx carrier connection</p>
                </div>
                <button
                  onClick={() => setShowWizard(true)}
                  className="px-4 py-2 rounded-xl bg-blue-600 hover:bg-blue-500 text-white text-sm font-medium transition-all">
                  Reconfigure
                </button>
              </div>
            </div>
          </div>
        )}
      </main>

      {/* Upgrade modal — shown when user tries to access a gated feature */}
      {upgradeFeature && (
        <UpgradeModal
          feature={upgradeFeature}
          theme={theme}
          onClose={() => setUpgradeFeature(null)}
        />
      )}

      {/* Onboarding wizard — auto-triggered or manually launched from Settings */}
      {showWizard && (
        <OnboardingWizard
          theme={theme}
          onComplete={() => setShowWizard(false)}
          onDismiss={() => setShowWizard(false)}
        />
      )}
    </div>
  );
}
